"use client"

import { useState, useEffect, useRef } from "react"
import DashboardLayout from "../layouts/DashboardLayout"
import { useDispatch, useSelector } from "react-redux"
import { getEmailHistoryDetailAction, getEmailHistoryListAction } from "../../services/action/common"
import {
  Search,
  Calendar,
  X,
  Mail,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  User,
  Copy,
  Users,
  FileText,
  ExternalLink,
  RefreshCw,
} from "react-feather"
import { format, parseISO } from "date-fns"

const EmailHistory = () => {
  const { user } = useSelector((state) => state.authenticationReducer)
  const { emailHistory, emailHistoryDetail } = useSelector((state) => state.commonReducer)
  const dispatch = useDispatch()

  // State for filters
  const [search, setSearch] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [sortField, setSortField] = useState("sentAt")
  const [sortDirection, setSortDirection] = useState("desc")
  const [isLoading, setIsLoading] = useState(true)

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState("details") // details, preview, recipients
  const [copiedMessage, setCopiedMessage] = useState("")

  // Refs
  const modalRef = useRef(null)
  const iframeRef = useRef(null)

  // Fetch data on initial load
  useEffect(() => {
    if (user?.userType) {
      fetchData()
    }
  }, [user])

  // Close modal on escape key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && isModalOpen) {
        setIsModalOpen(false)
      }
    }

    window.addEventListener("keydown", handleEscKey)
    return () => window.removeEventListener("keydown", handleEscKey)
  }, [isModalOpen])

  // Handle outside click for modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && isModalOpen) {
        setIsModalOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [modalRef, isModalOpen])

  // Update iframe content when email content changes
  useEffect(() => {
    if (iframeRef.current && emailHistoryDetail?.data?.emailContent && modalType === "preview") {
      const iframe = iframeRef.current
      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document

      iframeDocument.open()
      iframeDocument.write(emailHistoryDetail.data.emailContent)
      iframeDocument.close()

      // Make links open in new tab
      const links = iframeDocument.getElementsByTagName("a")
      for (let i = 0; i < links.length; i++) {
        links[i].setAttribute("target", "_blank")
      }
    }
  }, [emailHistoryDetail, modalType, isModalOpen])

  // Fetch data when filters change
  const fetchData = (page = currentPage) => {
    setIsLoading(true)
    dispatch(
      getEmailHistoryListAction({
        userType: user?.userType?.toLowerCase(),
        data: {
          page,
          search: search || null,
          startDate: startDate || null,
          endDate: endDate || null,
          sortField,
          sortDirection,
        },
        cb: () => {
          setIsLoading(false)
        },
      }),
    )
  }

  // Apply filters
  const applyFilters = () => {
    setCurrentPage(1)
    fetchData(1)
    if (window.innerWidth < 768) {
      setShowFilters(false)
    }
  }

  // Reset filters
  const resetFilters = () => {
    setSearch("")
    setStartDate("")
    setEndDate("")
    setCurrentPage(1)
    fetchData(1)
    if (window.innerWidth < 768) {
      setShowFilters(false)
    }
  }

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page)
    fetchData(page)
  }

  // Handle sorting
  const handleSort = (field) => {
    const direction = field === sortField && sortDirection === "asc" ? "desc" : "asc"
    setSortField(field)
    setSortDirection(direction)
    fetchData(currentPage)
  }

  // Get status counts
  const getStatusCounts = () => {
    if (!emailHistory?.data) return { total: 0, completed: 0, failed: 0, partial: 0 }

    const total = emailHistory.data.length
    const completed = emailHistory.data.filter((item) => item.status === "COMPLETED").length
    const failed = emailHistory.data.filter((item) => item.status === "FAILED").length
    const partial = emailHistory.data.filter((item) => item.status === "PARTIAL").length

    return { total, completed, failed, partial }
  }

  const statusCounts = getStatusCounts()

  // Generate pagination
  const renderPagination = () => {
    if (!emailHistory?.pagination) return null

    const { currentPage, totalPages } = emailHistory.pagination
    if (totalPages <= 1) return null

    const pageNumbers = []
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      if (currentPage <= 3) {
        pageNumbers.push(1, 2, 3, 4, "...", totalPages)
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
      } else {
        pageNumbers.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages)
      }
    }

    return (
      <div className="flex items-center justify-between mt-4 px-4">
        <div className="text-sm text-gray-600">
          Showing {emailHistory.data.length} of {emailHistory.pagination.totalCount} results
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-md ${currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "text-blue-600 hover:bg-blue-50"}`}
          >
            <ChevronLeft size={18} />
          </button>

          {pageNumbers.map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === "number" && handlePageChange(page)}
              className={`w-8 h-8 flex items-center justify-center rounded-md ${
                page === currentPage
                  ? "bg-blue-600 text-white"
                  : page === "..."
                    ? "text-gray-500 cursor-default"
                    : "text-gray-700 hover:bg-blue-50"
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-md ${currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : "text-blue-600 hover:bg-blue-50"}`}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    )
  }

  // Render status badge
  const renderStatusBadge = (status) => {
    switch (status) {
      case "COMPLETED":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle size={12} className="mr-1" /> Completed
          </span>
        )
      case "FAILED":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle size={12} className="mr-1" /> Failed
          </span>
        )
      case "PARTIAL":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertTriangle size={12} className="mr-1" /> Partial
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        )
    }
  }

  // Format date
  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy h:mm a")
    } catch (error) {
      return dateString
    }
  }

  // Render loading skeleton
  const renderSkeleton = () => (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg p-4 shadow animate-pulse">
            <div className="h-10 w-10 bg-gray-200 rounded-md mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 flex items-center px-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-4 bg-gray-200 rounded mx-2"
                style={{ width: `${Math.floor(Math.random() * 15) + 10}%` }}
              ></div>
            ))}
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="border-t border-gray-200 h-16 px-4 flex items-center">
              {[1, 2, 3, 4, 5].map((j) => (
                <div
                  key={j}
                  className="h-4 bg-gray-200 rounded mx-2"
                  style={{ width: `${Math.floor(Math.random() * 15) + 10}%` }}
                ></div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  )

  // View campaign details
  const viewDetail = (id, type = "details") => {
    dispatch(
      getEmailHistoryDetailAction({
        userType: user?.userType?.toLowerCase(),
        id,
        cb: () => {
          setIsModalOpen(true)
          setModalType(type)
        },
        errorCb: () => {
          // Handle error
        },
      }),
    )
  }

  // Copy to clipboard
  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedMessage(message)
        setTimeout(() => setCopiedMessage(""), 2000)
      },
      () => {
        setCopiedMessage("Failed to copy")
        setTimeout(() => setCopiedMessage(""), 2000)
      },
    )
  }

  // Render detail modal
  const renderDetailModal = () => {
    if (!isModalOpen || !emailHistoryDetail?.data) return null

    const campaign = emailHistoryDetail.data

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div
          ref={modalRef}
          className="bg-white rounded-xl shadow-2xl overflow-hidden w-full max-w-5xl max-h-[95vh] relative"
          style={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
        >
          {/* Close button */}
          <button
            onClick={() => setIsModalOpen(false)}
            className="absolute top-4 right-4 p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full z-10 transition-colors"
            title="Close"
          >
            <X size={18} />
          </button>

          {/* Modal Content */}
          <div className="max-h-[95vh] overflow-y-auto">
            {/* Details View */}
            {modalType === "details" && (
              <div className="p-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Campaign Info */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Campaign Name</p>
                        <div className="flex items-center mt-1">
                          <p className="text-base font-medium text-gray-900">{campaign.campaignName}</p>
                          <button
                            onClick={() => copyToClipboard(campaign.campaignName, "Campaign name copied!")}
                            className="ml-2 text-gray-400 hover:text-gray-600"
                            title="Copy campaign name"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Blog Title</p>
                        <p className="text-base font-medium text-gray-900 mt-1">{campaign.blogTitle}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Created At</p>
                        <p className="text-base font-medium text-gray-900 mt-1">{formatDate(campaign.createdAt)}</p>
                      </div>
                    </div>

                    {/* Delivery Stats */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Total Recipients</p>
                        <div className="flex items-center mt-1">
                          <p className="text-2xl font-bold text-gray-900">{campaign.totalRecipients}</p>
                          <button
                            onClick={() => setModalType("recipients")}
                            className="ml-2 text-gray-400 hover:text-gray-600"
                            title="View recipients"
                          >
                            <Users size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="flex space-x-8">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Successful</p>
                          <p className="text-2xl font-bold text-green-600 mt-1">{campaign.successCount}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Failed</p>
                          <p className="text-2xl font-bold text-red-600 mt-1">{campaign.failedCount}</p>
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex flex-col items-center justify-center bg-white rounded-lg p-6 shadow-sm">
                      <p className="text-sm font-medium text-gray-500 mb-2">Campaign Status</p>
                      <div className="mb-4">
                        {campaign.status === "COMPLETED" ? (
                          <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800">
                            <CheckCircle size={20} className="mr-2" /> Completed
                          </div>
                        ) : campaign.status === "FAILED" ? (
                          <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-800">
                            <XCircle size={20} className="mr-2" /> Failed
                          </div>
                        ) : (
                          <div className="inline-flex items-center px-4 py-2 rounded-full bg-yellow-100 text-yellow-800">
                            <AlertTriangle size={20} className="mr-2" /> Partial
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 text-center">
                        {campaign.status === "COMPLETED"
                          ? "All emails were sent successfully"
                          : campaign.status === "FAILED"
                            ? "The campaign failed to send any emails"
                            : "Some emails were sent successfully, others failed"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <button
                    onClick={() => setModalType("preview")}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Eye size={16} className="mr-2" /> View Email Template
                  </button>
                  <button
                    onClick={() => setModalType("recipients")}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Users size={16} className="mr-2" /> View Recipients
                  </button>
                  <button
                    onClick={() => copyToClipboard(campaign.emailContent, "HTML content copied!")}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Copy size={16} className="mr-2" /> Copy HTML
                  </button>
                  <button
                    onClick={() => window.open(`mailto:?subject=${encodeURIComponent(campaign.blogTitle)}`, "_blank")}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <ExternalLink size={16} className="mr-2" /> Share
                  </button>
                </div>

                {/* Campaign Summary */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Summary</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Campaign Name</span>
                      <span className="font-medium">{campaign.campaignName}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Blog Title</span>
                      <span className="font-medium">{campaign.blogTitle}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Sent Date</span>
                      <span className="font-medium">{formatDate(campaign.sentAt)}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Total Recipients</span>
                      <span className="font-medium">{campaign.totalRecipients}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Successful Deliveries</span>
                      <span className="font-medium text-green-600">{campaign.successCount}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <span className="text-gray-600">Failed Deliveries</span>
                      <span className="font-medium text-red-600">{campaign.failedCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Status</span>
                      <span>{renderStatusBadge(campaign.status)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recipients View */}
            {modalType === "recipients" && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Recipients List</h3>
                  <button
                    onClick={() =>
                      copyToClipboard(campaign.recipients.map((r) => r.email).join(", "), "All email addresses copied!")
                    }
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Copy size={16} className="mr-2" /> Copy All Emails
                  </button>
                </div>

                {/* Stats Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm font-medium text-gray-500">Total Recipients</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{campaign.recipients.length}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm font-medium text-gray-500">Successful Deliveries</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {campaign.recipients.filter((r) => r.status === "SENT").length}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm font-medium text-gray-500">Failed Deliveries</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">
                      {campaign.recipients.filter((r) => r.status !== "SENT").length}
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Name
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Email
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Status
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {campaign.recipients.map((recipient) => (
                          <tr key={recipient._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <User size={18} className="text-blue-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{recipient.name || "Unknown"}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{recipient.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {recipient.status === "SENT" ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle size={12} className="mr-1" /> Sent
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <XCircle size={12} className="mr-1" /> Failed
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => copyToClipboard(recipient.email, `${recipient.email} copied!`)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Copy Email"
                                >
                                  <Copy size={16} />
                                </button>
                                <button
                                  onClick={() => window.open(`mailto:${recipient.email}`, "_blank")}
                                  className="text-gray-600 hover:text-gray-900"
                                  title="Send Email"
                                >
                                  <Mail size={16} />
                                </button>
                                {recipient.status !== "SENT" && (
                                  <button className="text-yellow-600 hover:text-yellow-900" title="Resend Email">
                                    <RefreshCw size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Preview View */}
            {modalType === "preview" && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Email Template Preview</h3>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => copyToClipboard(campaign.emailContent, "HTML content copied!")}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Copy size={16} className="mr-2" /> Copy HTML
                    </button>
                    <button
                      onClick={() =>
                        window.open(
                          `data:text/html;charset=utf-8,${encodeURIComponent(campaign.emailContent)}`,
                          "_blank",
                        )
                      }
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <ExternalLink size={16} className="mr-2" /> Open in New Tab
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm h-[600px]">
                  <iframe
                    ref={iframeRef}
                    title="Email Preview"
                    className="w-full h-full border-0"
                    sandbox="allow-same-origin"
                  ></iframe>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Copy confirmation */}
        {copiedMessage && (
          <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in-up">
            <div className="flex items-center">
              <CheckCircle size={18} className="mr-2 text-green-400" />
              {copiedMessage}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* Header and Filters - Optimized for tablet */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div className="mb-4 md:mb-0">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Email Campaign History</h1>
            <p className="text-gray-600 mt-1">View and manage your email campaign history</p>
          </div>

          {/* Desktop Filters */}
          <div className="hidden md:flex md:items-center md:space-x-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search campaigns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
              />
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            <div className="relative">
              <input
                type="date"
                placeholder="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
              />
              <Calendar size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            <div className="relative">
              <input
                type="date"
                placeholder="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
              />
              <Calendar size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            <button
              onClick={applyFilters}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
            >
              Apply Filters
            </button>

            <button
              onClick={resetFilters}
              className="flex items-center text-gray-600 hover:text-gray-800 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <X size={16} className="mr-1" /> Reset
            </button>
          </div>

          {/* Mobile Filter Toggle */}
          <div className="md:hidden flex justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center bg-white border border-gray-300 rounded-md px-4 py-2 text-gray-600"
            >
              <Filter size={16} className="mr-2" /> Filters
            </button>

            <button
              onClick={resetFilters}
              className="flex items-center ml-2 text-gray-600 px-3 py-2 border border-gray-300 rounded-md"
            >
              <X size={16} className="mr-1" /> Reset
            </button>
          </div>
        </div>

        {/* Mobile Filters */}
        {showFilters && (
          <div className="md:hidden bg-white rounded-lg shadow-md p-4 mb-6 space-y-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search campaigns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
              />
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-3 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-3 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>
            </div>

            <button
              onClick={applyFilters}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
            >
              Apply Filters
            </button>
          </div>
        )}

        {/* Stats Cards - Optimized for tablet */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-blue-500">
            <div className="flex items-start">
              <div className="bg-blue-100 p-2 rounded-md">
                <Mail size={20} className="text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Campaigns</p>
                <p className="text-2xl font-bold text-gray-800">{statusCounts.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-green-500">
            <div className="flex items-start">
              <div className="bg-green-100 p-2 rounded-md">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-gray-800">{statusCounts.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-red-500">
            <div className="flex items-start">
              <div className="bg-red-100 p-2 rounded-md">
                <XCircle size={20} className="text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-gray-800">{statusCounts.failed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow border-l-4 border-yellow-500">
            <div className="flex items-start">
              <div className="bg-yellow-100 p-2 rounded-md">
                <AlertTriangle size={20} className="text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Partial</p>
                <p className="text-2xl font-bold text-gray-800">{statusCounts.partial}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          renderSkeleton()
        ) : emailHistory?.data?.length > 0 ? (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort("blogTitle")}
                      >
                        <div className="flex items-center">
                          Blog Title
                          {sortField === "blogTitle" &&
                            (sortDirection === "asc" ? (
                              <ChevronUp size={16} className="ml-1" />
                            ) : (
                              <ChevronDown size={16} className="ml-1" />
                            ))}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort("sentAt")}
                      >
                        <div className="flex items-center">
                          Sent Date
                          {sortField === "sentAt" &&
                            (sortDirection === "asc" ? (
                              <ChevronUp size={16} className="ml-1" />
                            ) : (
                              <ChevronDown size={16} className="ml-1" />
                            ))}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Recipients
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Success / Failed
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {emailHistory.data.map((campaign) => (
                      <tr key={campaign._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{campaign.blogTitle}</div>
                          <div className="text-sm text-gray-500">Blog: {campaign.campaignName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDate(campaign.sentAt)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.totalRecipients}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-green-600 font-medium">{campaign.successCount}</span>
                            <span className="mx-1">/</span>
                            <span className="text-red-600 font-medium">{campaign.failedCount}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{renderStatusBadge(campaign.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              className="text-blue-600 hover:text-blue-900"
                              onClick={() => viewDetail(campaign._id, "details")}
                              title="View Details"
                            >
                              <FileText size={18} />
                            </button>
                            <button
                              className="text-purple-600 hover:text-purple-900"
                              onClick={() => viewDetail(campaign._id, "preview")}
                              title="View Email Template"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              className="text-indigo-600 hover:text-indigo-900"
                              onClick={() => viewDetail(campaign._id, "recipients")}
                              title="View Recipients"
                            >
                              <Users size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {emailHistory.data.map((campaign) => (
                <div key={campaign._id} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 line-clamp-2">{campaign.blogTitle}</h3>
                        <p className="text-sm text-gray-500 mt-1">{formatDate(campaign.sentAt)}</p>
                      </div>
                      {renderStatusBadge(campaign.status)}
                    </div>
                  </div>

                  <div className="px-4 py-3 bg-gray-50">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Recipients</p>
                        <p className="text-sm font-medium">{campaign.totalRecipients}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Success / Failed</p>
                        <div className="flex items-center">
                          <span className="text-sm text-green-600 font-medium">{campaign.successCount}</span>
                          <span className="mx-1">/</span>
                          <span className="text-sm text-red-600 font-medium">{campaign.failedCount}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-4 py-3 border-t border-gray-200 flex justify-between">
                    <div className="flex space-x-2">
                      <button
                        className="flex items-center text-sm text-blue-600"
                        onClick={() => viewDetail(campaign._id, "details")}
                      >
                        <FileText size={16} className="mr-1" /> Details
                      </button>
                      <button
                        className="flex items-center text-sm text-purple-600"
                        onClick={() => viewDetail(campaign._id, "preview")}
                      >
                        <Eye size={16} className="mr-1" /> Preview
                      </button>
                    </div>
                    <button
                      className="flex items-center text-sm text-indigo-600"
                      onClick={() => viewDetail(campaign._id, "recipients")}
                    >
                      <Users size={16} className="mr-1" /> Recipients
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {renderPagination()}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Mail size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No email campaigns found</h3>
            <p className="text-gray-500 mb-4">There are no email campaigns matching your filters.</p>
            <button
              onClick={resetFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <X size={16} className="mr-2" /> Clear Filters
            </button>
          </div>
        )}

        {/* Detail Modal */}
        {renderDetailModal()}
      </div>
    </DashboardLayout>
  )
}

export default EmailHistory
